import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateListing, type Listing } from '../../../features/marketplace/api';
import { Button } from '../../../components/ui/Button';
import { ImageUpload } from '../../../components/upload/ImageUpload';

interface EditListingFormProps {
  listing: Listing;
}

export function EditListingForm({ listing }: EditListingFormProps) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(listing.title);
  const [description, setDescription] = useState(listing.description);
  const [price, setPrice] = useState((listing.priceCents / 100).toString());
  const [currency, setCurrency] = useState(listing.currency);
  const [duration, setDuration] = useState(listing.durationMinutes?.toString() || '60');
  const [workingDays, setWorkingDays] = useState<string[]>(listing.workingDays || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']);
  const [workStartTime, setWorkStartTime] = useState(listing.workStartTime || '09:00');
  const [workEndTime, setWorkEndTime] = useState(listing.workEndTime || '18:00');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaUrls, setMediaUrls] = useState<string[]>(listing.media || []);
  const [discussionThreadUrl, setDiscussionThreadUrl] = useState(
    listing.discussionThreadId ? `/forum/threads/${listing.discussionThreadId}` : ''
  );
  const [discussionThreadId, setDiscussionThreadId] = useState<string | null>(listing.discussionThreadId || null);
  const [discussionThreadTitle, setDiscussionThreadTitle] = useState<string | null>(listing.discussionThreadTitle || null);
  const [fetchingThread, setFetchingThread] = useState(false);
  const [whatsapp, setWhatsapp] = useState(listing.whatsapp || '');
  const [wechat, setWechat] = useState(listing.wechat || '');
  const [telegram, setTelegram] = useState(listing.telegram || '');
  const [phoneNumber, setPhoneNumber] = useState(listing.phoneNumber || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleReorder(newOrder: number[]) {
    // Reorder both mediaUrls and mediaFiles according to the new order
    const allMedia = [...mediaUrls, ...mediaFiles];
    const reorderedMedia = newOrder.map(i => allMedia[i]);

    // Split back into URLs and Files
    const newMediaUrls: string[] = [];
    const newMediaFiles: File[] = [];

    reorderedMedia.forEach(item => {
      if (typeof item === 'string') {
        newMediaUrls.push(item);
      } else if (item instanceof File) {
        newMediaFiles.push(item);
      }
    });

    setMediaUrls(newMediaUrls);
    setMediaFiles(newMediaFiles);
  }

  async function handleFetchThreadDetails(url: string) {
    if (!url.trim()) {
      setDiscussionThreadId(null);
      setDiscussionThreadTitle(null);
      return;
    }

    setFetchingThread(true);

    // Parse thread ID from URL (e.g., /forum/thread/123 or /topics/45/thread/123)
    const threadIdMatch = url.match(/\/threads?\/([a-zA-Z0-9-]+)/);
    if (!threadIdMatch) {
      setDiscussionThreadId(null);
      setDiscussionThreadTitle(null);
      setError('Invalid forum thread URL');
      setFetchingThread(false);
      return;
    }

    const threadId = threadIdMatch[1];
    setDiscussionThreadId(threadId);

    // Try to fetch thread details from forum API, but keep the ID even if it fails
    try {
      const response = await fetch(`/api/forum/threads/${threadId}`);
      if (!response.ok) {
        // Keep the thread ID but use a generic title
        setDiscussionThreadTitle('Discussion Thread');
        setError(null); // Clear any previous errors
      } else {
        const threadData = await response.json();
        setDiscussionThreadTitle(threadData.title || 'Discussion Thread');
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch thread details:', err);
      // Keep the thread ID but use a generic title
      setDiscussionThreadTitle('Discussion Thread');
      setError(null); // Don't show error if we can still save the thread ID
    } finally {
      setFetchingThread(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !price) {
      setError('Title, description, and price are required');
      return;
    }

    const priceCents = Math.round(parseFloat(price) * 100);
    if (isNaN(priceCents) || priceCents <= 0) {
      setError('Please enter a valid price');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const durationMinutes = duration ? parseInt(duration, 10) : undefined;

      // When seller edits, status changes back to pending_approval
      await updateListing(listing.id, {
        title: title.trim(),
        description: description.trim(),
        priceCents,
        currency,
        durationMinutes,
        workingDays: workingDays.length > 0 ? workingDays : undefined,
        workStartTime: workStartTime || undefined,
        workEndTime: workEndTime || undefined,
        discussionThreadId: discussionThreadId ?? "",
        discussionThreadTitle: discussionThreadTitle ?? "",
        whatsapp: whatsapp.trim() || undefined,
        wechat: wechat.trim() || undefined,
        telegram: telegram.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        media: [...mediaUrls],
        mediaFiles,
        status: 'pending_approval', // Reset to pending after edit
      });

      navigate('/marketplace/seller');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update listing');
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancel() {
    navigate('/marketplace/seller');
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">Edit Listing</h2>

          {/* Notice about re-approval */}
          <div className="bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-800 rounded p-3 mb-4">
            <p className="text-yellow-700 dark:text-yellow-400 text-sm">
              <strong>Note:</strong> After editing, this listing will return to "Pending Approval" status and require admin re-approval before appearing in the public catalog.
            </p>
          </div>

          {error && (
            <div className="bg-red-100 dark:bg-red-900/20 border border-red-300 dark:border-red-900 rounded p-3 mb-4 text-red-700 dark:text-red-400 text-sm">{error}</div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Title *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Description *</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                rows={4}
                required
              />
            </div>

            {/* Discussion Thread Link */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                💬 Discussion Thread (Optional)
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  value={discussionThreadUrl}
                  onChange={(e) => setDiscussionThreadUrl(e.target.value)}
                  onBlur={(e) => handleFetchThreadDetails(e.target.value)}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="Paste forum thread URL (e.g., /forum/threads/123)"
                  disabled={fetchingThread}
                />
                {fetchingThread && (
                  <p className="text-xs text-slate-600 dark:text-slate-400">📡 Fetching thread details...</p>
                )}
                {discussionThreadTitle && (
                  <div className="bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-800 rounded p-2">
                    <p className="text-xs text-green-700 dark:text-green-400">
                      ✓ Thread found: <span className="font-semibold">{discussionThreadTitle}</span>
                    </p>
                  </div>
                )}
                <p className="text-xs text-slate-500 dark:text-slate-500">
                  Link to a forum thread where buyers can discuss this service, ask questions, and see reviews
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Service Duration</label>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="45">45 minutes</option>
                <option value="60">1 hour</option>
                <option value="90">1.5 hours</option>
                <option value="120">2 hours</option>
                <option value="180">3 hours</option>
                <option value="240">4 hours</option>
                <option value="480">8 hours (full day)</option>
                <option value="">Custom / Not specified</option>
              </select>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                Helps buyers choose appropriate booking time slots
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Working Days</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                  <label key={day} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={workingDays.includes(day)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setWorkingDays([...workingDays, day]);
                        } else {
                          setWorkingDays(workingDays.filter(d => d !== day));
                        }
                      }}
                      className="rounded border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-brand-500 focus:ring-brand-500 focus:ring-offset-white dark:focus:ring-offset-slate-900"
                    />
                    <span className="capitalize">{day}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                Select the days you're available to provide this service
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Work Start Time</label>
                <input
                  type="time"
                  value={workStartTime}
                  onChange={(e) => setWorkStartTime(e.target.value)}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Work End Time</label>
                <input
                  type="time"
                  value={workEndTime}
                  onChange={(e) => setWorkEndTime(e.target.value)}
                  className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Contact Methods */}
            <div className="pt-6 border-t-2 border-purple-200 dark:border-purple-700">
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Contact Methods (Optional)
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-500 mb-4">
                Add contact methods so buyers can reach you directly
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    WhatsApp Number
                  </label>
                  <input
                    type="tel"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    placeholder="e.g., +1234567890"
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                    Include country code (e.g., +1, +44)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    WeChat ID
                  </label>
                  <input
                    type="text"
                    value={wechat}
                    onChange={(e) => setWechat(e.target.value)}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    placeholder="e.g., myWeChatID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Telegram Username
                  </label>
                  <input
                    type="text"
                    value={telegram}
                    onChange={(e) => setTelegram(e.target.value)}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    placeholder="e.g., @username"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                    placeholder="e.g., +1234567890"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Images</label>
              <ImageUpload
                onUpload={(files) => {
                  setMediaFiles((prev) => [...prev, ...files]);
                }}
                onFilesChange={setMediaFiles}
                maxFiles={10}
                maxSizeMB={5}
                existingUrls={mediaUrls}
                onRemove={(url) => {
                  setMediaUrls(mediaUrls.filter(u => u !== url));
                }}
                onReorder={handleReorder}
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Updating...' : 'Update Listing'}
            </Button>
            <Button type="button" onClick={handleCancel} variant="secondary">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
